import { IPermission } from '../../permissions/dtos/responses/permission.response';
import { TreeNode } from 'primeng/api';
import { Permission } from '../../permissions/models/permission.model';
import { ITypedLookup } from '../../common/models/permission-marker-lookup';
import { flattenTreeNodes } from '../utils/permission-groups.utils';

export interface IPermissionGroup extends ITypedLookup<string> {
    permissions: IPermission[];
    permissionGroups: IPermissionGroup[];
}

export class PermissionGroup implements IPermissionGroup {
    static PermissionGroupType = 'PermissionGroup';
    type: string = PermissionGroup.PermissionGroupType;
    public id: string;
    public englishName: string;
    public permissions: IPermission[];
    public permissionGroups: IPermissionGroup[];

    private constructor(permissionGroup: IPermissionGroup) {
        this.id = permissionGroup.id;
        this.englishName = permissionGroup.englishName;
        this.permissions = permissionGroup.permissions;
        this.permissionGroups = permissionGroup.permissionGroups;
    }

    static create(permissionGroup: IPermissionGroup): PermissionGroup {
        return new PermissionGroup(permissionGroup);
    }

    static createPermissionGroups(permissionGroups: IPermissionGroup[]): PermissionGroup[] {
        return permissionGroups.map(this.create);
    }

    static createAsTreeNode(permissionGroup: IPermissionGroup): TreeNode<ITypedLookup<string>> {
        return PermissionGroup.create(permissionGroup).toTreeNode();
    }

    static createAsTreeNodes(permissionGroups: IPermissionGroup[]): TreeNode<ITypedLookup<string>>[] {
        return permissionGroups.map(this.createAsTreeNode);
    }

    static createAsFlattenedTree(permissionGroups: IPermissionGroup[]): TreeNode<ITypedLookup<string>>[] {
        return flattenTreeNodes(PermissionGroup.createAsTreeNodes(permissionGroups));
    }

    public toTreeNode(): TreeNode<ITypedLookup<string>> {
        const permissionNodes: TreeNode<ITypedLookup<string>>[] = Permission.createAsTreeNodes(this.permissions);
        const groupNodes: TreeNode<ITypedLookup<string>>[] = PermissionGroup.createAsTreeNodes(this.permissionGroups);

        return {
            key: this.id,
            label: this.getReadableName(),
            data: this,
            children: [...permissionNodes, ...groupNodes],
            expandedIcon: 'pi pi-folder-open',
            collapsedIcon: 'pi pi-folder',
            leaf: permissionNodes.length == 0 && groupNodes.length == 0,
            selectable: true
        };
    }

    public getReadableName() {
        return this.englishName.getLastPart('.');
    }

    public containsPermissionGroup(id: string): boolean {
        if (this.permissionGroups.some(group => group.id.toLowerCase() === id.toLowerCase())) {
            return true;
        }

        for (const group of this.permissionGroups) {
            let g = PermissionGroup.create(group);
            if (g.containsPermissionGroup && g.containsPermissionGroup(id)) {
                return true;
            }
        }

        return false;
    }

    public containsPermission(id: string): boolean {
        if (this.permissions.some(permission => permission.id.toLowerCase() === id.toLowerCase())) {
            return true;
        }

        for (const group of this.permissionGroups) {
            let g = PermissionGroup.create(group);
            if (g.containsPermission && g.containsPermission(id)) {
                return true;
            }
        }
        return false;
    }

    public getPermissions(): IPermission[] {
        let allPermissions = [...this.permissions];

        for (const group of this.permissionGroups) {
            let g = PermissionGroup.create(group);
            allPermissions = [...allPermissions, ...g.getPermissions()];
        }

        return allPermissions;
    }

    public existsAsRoot(permissionGroups: IPermissionGroup[]): boolean {
        return Boolean(permissionGroups.find(x => x.id.toLowerCase() === this.id.toLowerCase()));
    }

    public exists(permissionGroups: IPermissionGroup[]): boolean {
        return permissionGroups.map(PermissionGroup.create).some(group => group.containsPermissionGroup(this.id));
    }
}
